/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { request } from '../services/api';

function softMessage(error) {
  const msg = String(error?.message || '').toLowerCase();
  if (msg.includes('not your turn')) return 'Abhi aapki turn nahi hai.';
  if (msg.includes('cannot move right now') || msg.includes('cannot roll right now') || msg.includes('roll dice first')) return 'Abhi yeh action allowed nahi hai.';
  if (msg.includes('invalid token move')) return 'Yeh token abhi move nahi ho sakta.';
  return error?.message || 'Action rejected.';
}

export function useRealtimeMatch(currentMatch, profile) {
  const [gameState, setGameState] = useState(currentMatch?.gameState || null);
  const [playerConnections, setPlayerConnections] = useState({});
  const isOnlineMatch = Boolean(currentMatch?.roomId && profile?.mobile);

  useEffect(() => {
    setGameState(currentMatch?.gameState || null);
  }, [currentMatch?.roomId]);

  useEffect(() => {
    if (!isOnlineMatch) return undefined;
    let cancelled = false;

    async function fetchState() {
      try {
        const result = await request(`/api/matches/state/${encodeURIComponent(currentMatch.roomId)}?mobile=${encodeURIComponent(profile.mobile)}`);
        if (cancelled) return;
        setGameState(result.gameState || result.match?.gameState || null);
        const connections = {};
        (result.gameState?.players || []).forEach((player) => {
          connections[player.id] = Boolean(player.connected);
        });
        setPlayerConnections(connections);
      } catch (error) {
        if (!cancelled) {
          setGameState((prev) => (prev ? { ...prev, message: error.message || prev.message } : prev));
        }
      }
    }

    const diceVisible = Boolean(gameState?.lastDiceVisibleUntil && Date.now() < gameState.lastDiceVisibleUntil);
    const fastPolling = gameState?.phase === 'transition' || diceVisible;
    const pollMs = fastPolling ? 450 : 1200;

    fetchState();
    const id = setInterval(fetchState, pollMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isOnlineMatch, currentMatch?.roomId, profile?.mobile, gameState?.phase, gameState?.lastDiceVisibleUntil]);

  const myPlayerId = useMemo(() => currentMatch?.playerId || null, [currentMatch?.playerId]);
  const isMyTurn = Boolean(gameState && myPlayerId && gameState.currentPlayerId === myPlayerId && gameState.status === 'active' && !gameState.matchEnded);
  const isRollPhase = gameState?.phase === 'roll';
  const isMovePhase = gameState?.phase === 'move';
  const isTransitionPhase = gameState?.phase === 'transition';

  async function rollDice() {
    if (!isOnlineMatch || !isMyTurn || !isRollPhase) return;
    try {
      const result = await request('/api/matches/roll', {
        method: 'POST',
        body: JSON.stringify({ roomId: currentMatch.roomId, mobile: profile.mobile }),
      });
      if (result?.gameState) setGameState(result.gameState);
    } catch (error) {
      setGameState((prev) => (prev ? { ...prev, message: softMessage(error) } : prev));
    }
  }

  async function moveToken(tokenIndex) {
    if (!isOnlineMatch || !isMyTurn || !isMovePhase) return;
    try {
      const result = await request('/api/matches/move', {
        method: 'POST',
        body: JSON.stringify({ roomId: currentMatch.roomId, mobile: profile.mobile, tokenIndex }),
      });
      if (result?.gameState) setGameState(result.gameState);
    } catch (error) {
      setGameState((prev) => (prev ? { ...prev, message: softMessage(error) } : prev));
    }
  }

  return {
    isOnlineMatch,
    gameState,
    myPlayerId,
    isMyTurn,
    isRollPhase,
    isMovePhase,
    isTransitionPhase,
    playerConnections,
    rollDice,
    moveToken,
  };
}
